import React, { useEffect, useState } from 'react';
import { Icon, Image, Label, Sidebar } from 'semantic-ui-react';
import { useResponsive } from 'ahooks';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'src/store';
import drawer, { DrawerName } from 'src/store/ui/drawer';
import { Typography } from 'src/components/coreUI/index';
import {
  fetchResources,
  markAllRead,
  markNotificationRead,
} from 'src/thunks/user_notification';
import { selectNotificationsHistory } from 'src/store/model/user_notification';
import { FULL_MONTH_HOUR, NOTIFICATION_TYPE, PATH } from 'src/constants/app';
import { htmlParser } from 'src/libs/htmlParser';
import dayjs from 'src/libs/dayjs';
import defaultAvatar from 'src/assets/default_profile.jpeg';
import logo from 'src/assets/logo_responsive.png';
import { fetchHomeworkCommentDetail } from 'src/thunks/homework_comment';
import { ENV } from 'src/env';
import { fetchHomeworkCommentReply } from 'src/thunks/homework_reply_comment';

export const drawer_name: DrawerName = 'notification_history';

const NotificationHistoryDrawer: React.FC = () => {
  const { visible, name } = useAppSelector((state) => state.ui.drawer);
  const {
    current_page = 0,
    total_pages = 0,
    next_page = 0,
  } = useAppSelector((state) => state.model.user_notification.pagination || {});
  const loading = useAppSelector(
    (state) => state.model.user_notification.loading,
  );
  const notificationHistory = useAppSelector(selectNotificationsHistory);
  const dispatch = useAppDispatch();
  const screen = useResponsive();

  useEffect(() => {
    dispatch(fetchResources({ page: 1, limit: 25 }));
  }, []);

  return (
    <Sidebar
      animation="overlay"
      icon="labeled"
      onHide={() => dispatch(drawer.close({ name: drawer_name }))}
      visible={visible && name === drawer_name}
      width={!screen.sm ? 'wide' : 'very wide'}
      direction={!screen.sm ? 'left' : 'right'}
      className="bg-white p-24"
    >
      <Typography.Header
        extra={
          <a
            className="cursor-pointer fs-16"
            onClick={() => dispatch(markAllRead())}
          >
            Mark All as Read
          </a>
        }
      >
        Notifications
      </Typography.Header>

      <div className="space-y-12">
        {notificationHistory.map((n) => (
          <NotificationItem key={n.id} {...n} />
        ))}

        {(current_page < total_pages || next_page !== null) && (
          <div
            className="text-gray text-center border-bottom cursor-pointer"
            onClick={() => {
              dispatch(fetchResources({ page: next_page!, limit: 25 }));
            }}
          >
            {loading && <Icon loading={loading} name="spinner" />} Load more
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export type ItemProps = Model.Notification;

export const NotificationItem: React.FC<ItemProps> = ({
  title,
  text,
  is_read,
  image_url,
  data,
  id,
  type,
  created_at,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleNotifyClick = async () => {
    setLoading(true);
    if (!is_read) {
      await dispatch(markNotificationRead({ id }));
    }

    if (
      data &&
      data.class_id &&
      data.class_topic_id &&
      data.class_topic_content_id
    ) {
      if (
        [
          NOTIFICATION_TYPE.new_discussion_mention,
          NOTIFICATION_TYPE.new_discussion_reply,
        ].includes(type as any) ||
        data.parent_id
      ) {
        navigate(
          `/${PATH.CLASS}/${data.class_id}/discussions/${data.class_topic_content_id}`,
          { state: data },
        );
      }
    }

    if (
      [
        NOTIFICATION_TYPE.new_homework_comment,
        NOTIFICATION_TYPE.new_homework_reply,
        NOTIFICATION_TYPE.new_homework_mention,
      ].includes(type as any)
    ) {
      if (data.user_assignment_file_id) {
        await dispatch(
          fetchHomeworkCommentDetail({
            id: data.user_assignment_file_comment_id,
          }),
        );
      }

      if (data.parent_id) {
        await dispatch(
          fetchHomeworkCommentReply({
            parent_id: data.parent_id,
            class_topic_id: data.class_topic_id,
          }),
        );
      }

      navigate(
        `/${PATH.CLASS}/${data.class_id}/homework/${data.class_topic_id}`,
        { state: data },
      );
    }

    if (NOTIFICATION_TYPE.new_user_issue === type) {
      window.open(`${ENV.ADMIN_URL}/${PATH.ISSUES}`, '_blank');
    }

    if (NOTIFICATION_TYPE.new_class_topic_content === type) {
      navigate(
        `/${PATH.CLASS}/${data.class_id}/${data.class_topic_id}/${data.class_topic_content_id}`,
      );
    }

    if (
      [
        NOTIFICATION_TYPE.new_class_topic,
        NOTIFICATION_TYPE.new_week_topic,
      ].includes(type as any)
    ) {
      navigate(`/${PATH.MY_COURSES}/${data.class_id}`, { state: data });
    }

    setLoading(false);
  };

  const isSystemNotify = [
    NOTIFICATION_TYPE.new_class_topic,
    NOTIFICATION_TYPE.new_class_topic_content,
    NOTIFICATION_TYPE.new_class_user,
    NOTIFICATION_TYPE.new_week_topic,
  ].includes(type as any);

  return (
    <div
      className="segment border p-16 border-radius-4 d-flex space-x-6 cursor-pointer justify-between"
      onClick={handleNotifyClick}
    >
      <div className="flex-shrink-0 mr-8">
        <Image
          src={image_url || (isSystemNotify ? logo : defaultAvatar)}
          width={40}
          height={40}
          circular
          className="object-fit-cover"
        />
      </div>

      <div className="flex-1">
        <span className="fs-16 mb-4">{htmlParser(text)}</span> <br />
        <span className="text-gray">
          {dayjs(created_at).format(FULL_MONTH_HOUR)}
        </span>
      </div>

      {!is_read && (
        <Label
          className="flex-shrink-0"
          circular
          empty
          color="purple"
          size="mini"
        />
      )}

      {loading && <Icon loading name="spinner" color="purple" />}
    </div>
  );
};

export default NotificationHistoryDrawer;
